import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, SectionCard, FormSection, Input, Skeleton, StatusBadge,
  DataTable, Pagination, type DataTableColumn,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  toast,
} from '@/components/ui';
import { ArrowLeft, Plus } from 'lucide-react';
import { useAllEventsQuery, useEventQuery } from '../hooks/useEventsQuery';
import { useAllEventWinnersQuery } from '../hooks/useAllEventWinnersQuery';
import {
  fetchEventWinner,
  useEventWinnersQuery,
  useEventWinnerMutations,
  WINNER_PAGE_SIZE,
  type EventWinnerSummary,
} from '../hooks/useEventWinnersQuery';
import { useEventImageUpload } from '../hooks/useEventImageUpload';
import {
  instantToLocalSeconds,
  localSecondsToInstant,
  localSecondsToDate,
} from '../lib/datetimeLocalSeconds';
import { LocaleHtmlTabs } from '../components/LocaleHtmlTabs';
import { type ContentLocale } from '../lib/contentLocales';
import { buildLocaleMultiFieldTranslations, pickLocaleHtml } from '../lib/localeContent';
import type { EventWinnerRequest, EventWinnerView, EventWinnerTranslation, WinnerDisplayStatus } from '../types/cms';
import { editorImageGuideFor } from '@/lib/upload/uploadGuide';

// QA-122 — 당첨자 발표 per-locale 필드 키(EventWinnerTranslation = {title, body}).
const WINNER_TITLE_FIELD = 'title';
const WINNER_BODY_FIELD = 'body';

interface FormState {
  /** QA-406 — 서버가 매긴 번호(수정 시 표시만). 새 글은 null — 등록하면 서버가 매긴다. */
  postNumber: number | null;
  title: string;
  author: string;
  body: string;
  // QA-124 — 발행(노출)일시. datetime-local(초 포함) 표시값 'YYYY-MM-DDTHH:mm:ss'.
  // 지정 시각부터 SF 공개(displayStartAt 게이트). 목록 표기 발표일(publishedAt)은 날짜 부분으로 파생.
  publishAtLocal: string;
  // QA-122 — locale 별 제목/본문(en/ja/zh). ko-KR 은 title/body 정본. 빈 값 = SF ko 폴백.
  titleTranslations: Partial<Record<ContentLocale, string>>;
  bodyTranslations: Partial<Record<ContentLocale, string>>;
  // QA-481 — 노출 상태. undefined = 응답에 필드가 없음(BE 반영 전) → 보내지 않는다(기존 값 유지).
  displayStatus: WinnerDisplayStatus | undefined;
  /** 예약 노출 종료(datetime-local 초 포함). 빈 값 = 종료 없음. */
  displayEndLocal: string;
}

// 현재 시각을 datetime-local(초 포함) 표시값으로. 신규 작성 기본값(즉시 노출 의미).
const nowLocalSeconds = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

const emptyForm = (): FormState => ({
  postNumber: null,
  title: '',
  author: '',
  body: '',
  publishAtLocal: nowLocalSeconds(),
  titleTranslations: {},
  bodyTranslations: {},
  displayStatus: 'ALWAYS',
  displayEndLocal: '',
});

/** QA-481 — 응답의 노출 상태·종료를 폼으로. 필드가 없으면 undefined(BE 반영 전). */
const displayFromView = (post: EventWinnerView): Pick<FormState, 'displayStatus' | 'displayEndLocal'> => ({
  displayStatus: post.displayStatus ?? undefined,
  displayEndLocal: post.displayEndAt ? instantToLocalSeconds(post.displayEndAt) : '',
});

const DISPLAY_STATUS_LABELS: Record<WinnerDisplayStatus, string> = {
  ALWAYS: '노출',
  SCHEDULED: '예약 노출',
  HIDDEN: '노출 안 함',
};

// QA-122 — 응답 게시글에서 locale 별 제목/본문(en/ja/zh) 추출. 정본 ko 는 title/body 본필드.
const titleTranslationsFromView = (post: EventWinnerView): Partial<Record<ContentLocale, string>> =>
  pickLocaleHtml(post.translations, WINNER_TITLE_FIELD);
const bodyTranslationsFromView = (post: EventWinnerView): Partial<Record<ContentLocale, string>> =>
  pickLocaleHtml(post.translations, WINNER_BODY_FIELD);

// QA-124 — 발행일시 표시값 결정: displayStartAt(노출 시각, 초 포함)이 있으면 그것을,
// 없으면 publishedAt(LocalDate) 자정으로 폴백(과거 데이터 호환).
const publishAtLocalFromView = (post: EventWinnerView): string => {
  if (post.displayStartAt) return instantToLocalSeconds(post.displayStartAt);
  return post.publishedAt ? `${post.publishedAt.slice(0, 10)}T00:00:00` : nowLocalSeconds();
};

// QA-124 — 목록 발행일시 라벨. displayStartAt(노출 시각) 이 있으면 'YYYY-MM-DD HH:mm:ss',
// 없으면 발표일(날짜만) 폴백. 잘못된 값은 원본 그대로 노출(가독 방어).
const formatPublishLabel = (post: Pick<EventWinnerView, 'displayStartAt' | 'publishedAt'>): string => {
  if (post.displayStartAt) {
    const local = instantToLocalSeconds(post.displayStartAt);
    return local ? local.replace('T', ' ') : post.publishedAt.slice(0, 10);
  }
  return post.publishedAt ? post.publishedAt.slice(0, 10) : '';
};

/**
 * 전체 게시판에서 새 글을 쓸 때 연결 이벤트를 고른다. 이벤트가 2천 건이 넘어 선택 박스 대신 이름 검색으로 좁힌다
 * (최근 시작한 이벤트부터 최대 30건).
 */
const EventPicker = ({
  events,
  value,
  onChange,
}: {
  events: ReadonlyArray<{ eventId: string; title: string; startDate?: string | null }>;
  value: string;
  onChange: (eventId: string) => void;
}) => {
  const [q, setQ] = useState('');
  const selected = events.find((e) => e.eventId === value);
  const matches = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return [...events]
      .filter((e) => !kw || e.title.toLowerCase().includes(kw))
      .sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? ''))
      .slice(0, 30);
  }, [events, q]);
  if (selected) {
    return (
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm" style={{ color: 'var(--content-strong)' }} title={selected.title} data-testid="winner-event-selected">
          {selected.title}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')}>
          변경
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="이벤트명으로 검색"
        aria-label="연결 이벤트 검색"
        data-testid="winner-event-search"
      />
      <ul
        role="listbox"
        aria-label="연결 이벤트"
        className="max-h-48 overflow-y-auto rounded-md border p-1"
        style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
      >
        {matches.length === 0 ? (
          <li className="px-2 py-3 text-center text-xs" style={{ color: 'var(--content-muted)' }}>
            검색 결과가 없습니다.
          </li>
        ) : (
          matches.map((e) => (
            <li key={e.eventId}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => onChange(e.eventId)}
                className="block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--surface-1)]"
                title={e.title}
                data-testid="winner-event-option"
              >
                {e.title}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

/** 발행 시각(노출 시작, 없으면 발표일 자정) — 목록 정렬 키. */
const publishSortKey = (post: Pick<EventWinnerView, 'displayStartAt' | 'publishedAt'>): number => {
  const t = Date.parse(post.displayStartAt ?? `${post.publishedAt?.slice(0, 10)}T00:00:00+09:00`);
  return Number.isFinite(t) ? t : 0;
};

/**
 * 당첨자 발표 게시판.
 *  - /cms/events/:eventId/winners — 그 이벤트의 글(작성·수정·삭제).
 *  - /cms/event-winners — 이벤트 구분 없는 전체 글(QA-407). 같은 화면에 [연결 이벤트] 열을 더하고, 작성은 이벤트별
 *    화면에서 한다(글은 이벤트에 속한다). 노출 예약 전 글도 나온다(admin 목록).
 * 제목을 누르면 수정 팝업, 행 끝은 [삭제]만 둔다.
 */
export const EventWinnerBoardPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const allMode = !eventId;
  // 전체 화면에서는 수정·삭제할 글의 이벤트로 mutation 경로를 잡는다.
  const [rowEventId, setRowEventId] = useState('');
  const id = eventId ?? rowEventId;

  const eventQuery = useEventQuery(eventId ?? '');
  const eventsQuery = useAllEventsQuery(allMode);
  const eventTitle = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of eventsQuery.data ?? []) m.set(e.eventId, e.title);
    return m;
  }, [eventsQuery.data]);
  // QA-407 — 목록은 페이지·요약(본문 없음)으로 받는다. 이관 글을 모은 숨김 캠페인은 전량이면 수백 MB 다.
  const [page, setPage] = useState(1);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const eventWinnersQuery = useEventWinnersQuery(eventId ?? '', page, keyword);
  const allWinnersQuery = useAllEventWinnersQuery(page, undefined, keyword, allMode);
  const winnersQuery = allMode ? allWinnersQuery : eventWinnersQuery;
  const { create, update, remove } = useEventWinnerMutations(id);
  const uploadImage = useEventImageUpload();

  // 발행일시 역순. BE 목록은 글번호 역순이라 페이지 안에서 다시 정렬한다(페이지 간 정렬은 BE 요청 — 전달 문서).
  const posts = useMemo(
    () => [...(winnersQuery.data?.rows ?? [])].sort((a, b) => publishSortKey(b) - publishSortKey(a) || b.postNumber - a.postNumber),
    [winnersQuery.data],
  );
  const total = winnersQuery.data?.total ?? 0;

  const [editing, setEditing] = useState<EventWinnerView | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(() => emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventWinnerSummary | null>(null);
  const [loadingPostId, setLoadingPostId] = useState<number | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const openCreate = () => {
    // 전체 화면에서는 연결 이벤트를 팝업에서 새로 고른다.
    if (allMode) setRowEventId('');
    setEditing(null);
    setForm(emptyForm());
    setError(null);
    setFormOpen(true);
  };

  // QA-407 — 목록 요약에는 본문·번역이 없어 글을 열 때 단건 GET 으로 받아 폼을 채운다.
  const openEdit = async (row: EventWinnerSummary) => {
    if (loadingPostId !== null) return;
    setLoadingPostId(row.winnerId);
    let post: EventWinnerView;
    try {
      post = await fetchEventWinner(row.eventId || id, row.winnerId);
      setRowEventId(row.eventId);
    } catch (e) {
      toast.error(friendlyMessage(e, '발표글을 불러오지 못했습니다.'));
      return;
    } finally {
      setLoadingPostId(null);
    }
    setEditing(post);
    setForm({
      postNumber: post.postNumber,
      title: post.title,
      author: post.author,
      body: post.body,
      publishAtLocal: publishAtLocalFromView(post),
      titleTranslations: titleTranslationsFromView(post),
      bodyTranslations: bodyTranslationsFromView(post),
      ...displayFromView(post),
    });
    setError(null);
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    setError(null);
    if (allMode && !editing && !rowEventId) {
      setError('연결 이벤트를 선택해 주세요.');
      return;
    }
    if (!form.title.trim() || !form.author.trim() || !form.body.trim()) {
      setError('제목 · 작성자 · 본문을 모두 입력해 주세요.');
      return;
    }
    // QA-124 — 발행일시(초 포함)를 노출 게이트 displayStartAt(Instant) 로,
    // 목록 표기 발표일 publishedAt(LocalDate) 은 그 날짜 부분으로 함께 전송한다.
    const displayStartAt = localSecondsToInstant(form.publishAtLocal);
    const publishedAt = localSecondsToDate(form.publishAtLocal) || form.publishAtLocal.slice(0, 10);
    if (!publishedAt) {
      setError('발행일시를 입력해 주세요.');
      return;
    }
    // QA-122 — locale 별 제목/본문을 translations 맵으로(빈 로케일 제외=ko 폴백). 모두 비면 생략.
    const translations = buildLocaleMultiFieldTranslations<EventWinnerTranslation>({
      [WINNER_TITLE_FIELD]: form.titleTranslations,
      [WINNER_BODY_FIELD]: form.bodyTranslations,
    });
    // QA-481 — 예약 노출이면 종료 시각(선택)을, 아니면 종료 없음. 종료는 발행(시작) 이후여야 한다.
    const displayEndAt =
      form.displayStatus === 'SCHEDULED' && form.displayEndLocal ? localSecondsToInstant(form.displayEndLocal) : null;
    if (displayEndAt && displayStartAt && Date.parse(displayEndAt) <= Date.parse(displayStartAt)) {
      setError('노출 종료일시는 발행일시보다 뒤여야 합니다.');
      return;
    }
    const body: EventWinnerRequest = {
      title: form.title.trim(),
      author: form.author.trim(),
      body: form.body,
      publishedAt,
      displayStartAt,
      ...(translations ? { translations } : {}),
      ...(form.displayStatus !== undefined ? { displayStatus: form.displayStatus, displayEndAt } : {}),
    };
    try {
      if (editing) {
        await update.mutateAsync({ winnerId: editing.winnerId, body });
        setFormOpen(false);
      } else {
        // QA-124 — 작성 직후 "수정 모드"로 전환(발행일시 재조정 등 후속 편집 가능).
        // 작성글이 곧바로 수정 화면으로 떠야 한다는 스펙(D-7).
        const created = await create.mutateAsync(body);
        setEditing(created);
        setForm({
          postNumber: created.postNumber,
          title: created.title,
          author: created.author,
          body: created.body,
          publishAtLocal: publishAtLocalFromView(created),
          titleTranslations: titleTranslationsFromView(created),
          bodyTranslations: bodyTranslationsFromView(created),
          ...displayFromView(created),
        });
        // 다이얼로그는 열어둔 채 제목만 '수정'으로 전환(editing 설정으로 자동 반영).
        toast.success('발표글을 등록했습니다. 수정 모드로 전환되었습니다.');
      }
    } catch (e) {
      setError(friendlyMessage(e, '저장에 실패했습니다.'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove.mutateAsync(deleteTarget.winnerId);
      setDeleteTarget(null);
    } catch {
      // 삭제 실패는 mutation 상태로 노출.
    }
  };

  const isBusy = create.isPending || update.isPending;

  const isScheduled = (row: EventWinnerSummary) =>
    !!row.displayStartAt && Date.parse(row.displayStartAt) > Date.now();

  const columns: DataTableColumn<EventWinnerSummary>[] = [
    {
      key: 'postNumber',
      header: '번호',
      align: 'center',
      width: 80,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.postNumber}
        </span>
      ),
    },
    {
      key: 'title',
      header: '제목',
      headerAlign: 'center',
      cell: (row) => (
        <button
          type="button"
          className={`block w-full ${allMode ? 'max-w-[380px]' : 'max-w-[520px]'} truncate text-left text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60`}
          style={{ color: 'var(--content-strong)' }}
          title={row.title}
          disabled={loadingPostId !== null}
          onClick={() => void openEdit(row)}
          data-testid="winner-title"
        >
          {loadingPostId === row.winnerId ? '불러오는 중…' : row.title}
        </button>
      ),
    },
    ...(allMode
      ? [
          {
            key: 'event',
            header: '연결 이벤트',
            headerAlign: 'center' as const,
            width: 220,
            cell: (row: EventWinnerSummary) => (
              <Link
                to={`/cms/events/${row.eventId}/winners`}
                className="block max-w-[200px] truncate text-xs hover:underline"
                style={{ color: 'var(--content-muted)' }}
                title={eventTitle.get(row.eventId) ?? row.eventId}
                data-testid="winner-event"
              >
                {eventTitle.get(row.eventId) ?? row.eventId}
              </Link>
            ),
          },
        ]
      : []),
    {
      key: 'author',
      header: '작성자',
      align: 'center',
      width: 120,
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-default)' }}>
          {row.author}
        </span>
      ),
    },
    {
      key: 'publishedAt',
      header: '발행일시',
      align: 'center',
      width: 200,
      cell: (row) => (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatPublishLabel(row)}
          {isScheduled(row) && <StatusBadge variant="info" label="예약" data-testid="winner-scheduled" />}
          {row.displayStatus === 'HIDDEN' ? (
            <StatusBadge variant="neutral" label="노출 안 함" data-testid="winner-hidden" />
          ) : row.displayStatus === 'SCHEDULED' && row.displayEndAt ? (
            <StatusBadge variant="info" label={`~${instantToLocalSeconds(row.displayEndAt).replace('T', ' ').slice(0, 16)}`} data-testid="winner-display-end" />
          ) : null}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '관리',
      align: 'center',
      width: 88,
      cell: (row) => (
        <Button
          variant="outline"
          size="sm"
          disabled={remove.isPending}
          onClick={() => {
            setRowEventId(row.eventId);
            setDeleteTarget(row);
          }}
          data-testid="winner-delete"
        >
          삭제
        </Button>
      ),
    },
  ];

  return (
    <PageShell
      title={allMode ? '당첨자 발표 게시판' : '당첨자 발표'}
      description={
        allMode
          ? '모든 이벤트의 당첨자 발표 글입니다(노출 예약 전 글 포함). [발표글 작성]에서 연결 이벤트를 골라 바로 쓸 수 있습니다.'
          : eventQuery.data
            ? eventQuery.data.title
            : undefined
      }
      data-testid="event-winner-board-page"
      actions={
        allMode ? (
          <Button size="sm" onClick={openCreate} data-testid="winner-create">
            <Plus size={15} aria-hidden="true" />
            발표글 작성
          </Button>
        ) : (
        <div className="flex gap-2">
          <Link to={`/cms/events/${id}/applications`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft size={14} className="mr-1" aria-hidden="true" />
              응모 현황
            </Button>
          </Link>
          <Button size="sm" onClick={openCreate}>
            <Plus size={15} aria-hidden="true" />
            발표글 작성
          </Button>
        </div>
        )
      }
    >
      <SectionCard
        title={`발표 게시글 (${total.toLocaleString()}건)`}
        aside={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setKeyword(keywordDraft.trim());
              setPage(1);
            }}
          >
            <Input
              aria-label="제목 검색"
              placeholder="제목 검색"
              value={keywordDraft}
              onChange={(e) => setKeywordDraft(e.target.value)}
              data-testid="winner-board-keyword"
            />
            <Button type="submit" size="sm" data-testid="winner-board-search">
              검색
            </Button>
          </form>
        }
      >
        {winnersQuery.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[44px] w-full" />
            ))}
          </div>
        ) : winnersQuery.isError ? (
          <div
            role="alert"
            aria-live="polite"
            className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>발표 게시글을 불러오지 못했습니다.</p>
            <Button variant="outline" size="sm" onClick={() => winnersQuery.refetch()}>
              다시 시도
            </Button>
          </div>
        ) : (
          <>
            <DataTable
              columns={[...columns]}
              rows={[...posts]}
              rowKey={(row) => row.winnerId}
              emptyState="등록된 발표 게시글이 없습니다."
              stickyHeader
            />
            <Pagination total={total} currentPage={page} pageSize={WINNER_PAGE_SIZE} onPageChange={setPage} />
          </>
        )}
      </SectionCard>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        {/* QA-408 — 본문 에디터가 좁아 팝업을 가로로 넓힌다(기본 max-w-lg 512px → 1024px). */}
        <DialogContent className="max-w-5xl" data-testid="winner-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? '발표글 수정' : '발표글 작성'}</DialogTitle>
            <DialogDescription>당첨자 발표 게시글을 작성합니다.</DialogDescription>
          </DialogHeader>

          {error && (
            <div className="rounded-md p-3 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
              {error}
            </div>
          )}

          <div className="flex flex-col gap-3">
            {allMode ? (
              <FormSection label="연결 이벤트" density="compact" required={!editing}>
                {editing ? (
                  <span className="text-sm" style={{ color: 'var(--content-default)' }} data-testid="winner-event-readonly">
                    {eventTitle.get(rowEventId) ?? rowEventId}
                  </span>
                ) : (
                  <EventPicker events={eventsQuery.data ?? []} value={rowEventId} onChange={setRowEventId} />
                )}
              </FormSection>
            ) : null}
            {/* QA-406 — 게시글 번호는 입력하지 않는다. 등록하면 서버가 전체 최대 + 1 로 매기고, 수정해도 바뀌지 않는다. */}
            <FormSection label="게시글 번호" density="compact">
              <span className="text-sm tabular-nums" style={{ color: 'var(--content-default)' }} data-testid="winner-post-number">
                {form.postNumber ?? '등록하면 자동으로 매겨집니다'}
              </span>
            </FormSection>
            <FormSection label="작성자" density="compact" required>
              <Input
                type="text"
                value={form.author}
                onChange={(e) => set('author', e.target.value)}
                maxLength={100}
                placeholder="에버라인"
                className="max-w-[240px]"
                required
              />
            </FormSection>
            <FormSection
              label="발행일시"
              density="compact"
              required
              hint="지정한 시각부터 스토어프론트에 공개됩니다. (초 단위까지 설정)"
            >
              <Input
                type="datetime-local"
                step={1}
                value={form.publishAtLocal}
                onChange={(e) => set('publishAtLocal', e.target.value)}
                className="max-w-[260px]"
                aria-label="발행일시"
              />
            </FormSection>
            {form.displayStatus !== undefined ? (
              <FormSection
                label="노출 상태"
                density="compact"
                hint="예약 노출은 발행일시부터 종료일시까지만 보입니다(종료를 비우면 계속). 노출 안 함은 스토어프론트에서 숨깁니다."
              >
                <div className="flex flex-col gap-2" data-testid="winner-display-status">
                  <div className="flex flex-wrap gap-4" role="radiogroup" aria-label="노출 상태">
                    {(['ALWAYS', 'SCHEDULED', 'HIDDEN'] as const).map((s) => (
                      <label key={s} className="inline-flex items-center gap-1.5 text-sm" style={{ color: 'var(--content-default)' }}>
                        <input
                          type="radio"
                          name="winner-display-status"
                          checked={form.displayStatus === s}
                          onChange={() => set('displayStatus', s)}
                          data-testid={`winner-display-${s}`}
                        />
                        {DISPLAY_STATUS_LABELS[s]}
                      </label>
                    ))}
                  </div>
                  {form.displayStatus === 'SCHEDULED' ? (
                    <Input
                      type="datetime-local"
                      step={1}
                      value={form.displayEndLocal}
                      onChange={(e) => set('displayEndLocal', e.target.value)}
                      className="max-w-[260px]"
                      aria-label="노출 종료일시"
                      data-testid="winner-display-end-input"
                    />
                  ) : null}
                </div>
              </FormSection>
            ) : null}
            <FormSection label="제목 · 본문" density="compact" required>
              {/* QA-122 — 발표 제목·본문을 로케일(ko/en/ja/zh)별로 입력. ko 정본 필수,
                  타 로케일 빈 값 = SF ko 폴백. */}
              <LocaleHtmlTabs
                baseValue={form.body}
                onBaseChange={(html) => set('body', html)}
                translations={form.bodyTranslations}
                onTranslationChange={(locale, html) =>
                  setForm((prev) => ({
                    ...prev,
                    bodyTranslations: { ...prev.bodyTranslations, [locale]: html },
                  }))
                }
                titleField={{
                  label: '제목',
                  baseValue: form.title,
                  onBaseChange: (v) => set('title', v),
                  translations: form.titleTranslations,
                  onTranslationChange: (locale, v) =>
                    setForm((prev) => ({
                      ...prev,
                      titleTranslations: { ...prev.titleTranslations, [locale]: v },
                    })),
                  placeholder: '해당 언어 발표 제목',
                  maxLength: 300,
                }}
                minHeight={220}
                onUploadImage={uploadImage}
                imageGuide={editorImageGuideFor('EVENT_WINNER_BODY')}
                onUploadError={(e) => setError(e instanceof Error ? e.message : '이미지 업로드에 실패했습니다.')}
                ariaLabelPrefix="발표 본문"
              />
            </FormSection>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button size="sm" onClick={() => void handleSubmit()} disabled={isBusy}>
              {editing ? '저장' : '등록'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>발표글 삭제</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.title ?? ''}" 게시글을 삭제하시겠습니까?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button variant="danger" size="sm" disabled={remove.isPending} onClick={() => void handleDelete()}>
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
